import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { appEnv } from '../../app.config';
import { BehaviorSubject, catchError, map, Observable, Subject } from 'rxjs';
import { ResponseModel } from '../models/response.model';
import { ClientModel } from '../models/common-resource/client.model';
import { DriverModel } from '../models/common-resource/driver.model';
import { VehicleModel } from '../models/common-resource/vehicle.model';
import _ from 'lodash';
import { ShiftTypeModel } from '../models/common-resource/shift-type.model';
import { ServiceTypeModel } from '../models/common-resource/service-type.model';
import { StateModel } from '../models/common-resource/state.model';
import { StopStatusModel } from '../models/common-resource/stop-status.model';
import { RosterStatusModel } from '../models/common-resource/roster-status.model';
import { TollOptionModel } from '../models/common-resource/toll-option.model';
import { UserModel } from '../models/common-resource/user.model';
import { StopTypeModel } from '../models/common-resource/stop-type.model';
import { CustomerModel } from '../models/common-resource/customer.model';
import { EmployeeTypeModel } from '../../modules/settings/model/employee-type.model';
import { VehicleClassModel } from '../models/common-resource/vehicle-class.model';
import { ScheduleTypeModel } from '../models/common-resource/schedule-type.model';
import { ServiceCategoryModel } from '../models/common-resource/service-category.model';
import { OptimisationModel } from '../models/common-resource/optimisation-type.model';
import { RoleModel } from '../models/common-resource/role.model';
import { DriverAccountTypeModel } from '../models/common-resource/driver-account-type.model';

@Injectable({
  providedIn: 'root'
})
export class CommonResourceService {
  private _clients: ClientModel[] = [];
  public rawClients: ClientModel[] = [];
  private _drivers: DriverModel[] = [];
  private _rawDrivers: DriverModel[] = [];
  private _irStatus: any[] = [];
  private _irTypes: any[] = [];
  private _rosterStatus: RosterStatusModel[] = [];
  public rawRosterStatus: RosterStatusModel[] = [];
  private _serviceTypes: ServiceTypeModel[] = [];
  public rawServiceTypes: ServiceTypeModel[] = [];
  private _shiftTypes: ShiftTypeModel[] = [];
  public rawShiftTypes: ShiftTypeModel[] = [];
  private _stopStatus: StopStatusModel[] = [];
  public rawStopStatus: StopStatusModel[] = [];
  private _stopSubStatus: ClientModel[] = [];
  private _stopTypes: StopTypeModel[] = [];
  public rawStopTypes: StopTypeModel[] = [];
  private _tollOptions: TollOptionModel[] = [];
  public rawTollOptions: TollOptionModel[] = [];
  private _users: UserModel[] = [];
  public rawUsers: UserModel[] = [];
  private _vehicles: VehicleModel[] = [];
  public rawVehicles: VehicleModel[] = [];
  private _states: StateModel[] = [];
  public rawStates: StateModel[] = [];
  
  private _customer: CustomerModel[] = [];
  private rawCustomer: CustomerModel[] = [];
  private _employeeTypes: EmployeeTypeModel[] = [];
  private _vehicleClass: VehicleClassModel[] = [];
  private _scheduleTypes: ScheduleTypeModel[] = [];
  private _serviceCategory: ServiceCategoryModel[] = [];
  private _optimisationTypes: OptimisationModel[] = [];
  private _roles: RoleModel[] = [];
  private _driverAccountTypes: DriverAccountTypeModel[] = [];

  public driverSubject = new BehaviorSubject<any>([]);
  public customerSubject = new BehaviorSubject<any>([]);
  public stateSubject = new BehaviorSubject<any>([]);
  public employeeTypeSubject = new BehaviorSubject<any>([]);

  public _paidType = [
    {id: 1, name: 'Hourly'},
    {id: 2, name: 'Per Service'}
  ];

  public _availability: Array<{id: number, name: string}> = [
    {id: 0, name: 'Select Status'}, /* Mostly id for newly created driver data */
    {id: 1, name: 'Unset Status'}, /* For approved contractors who currently do shifts/jobs */
    {id: 2, name: 'Standby'},
    {id: 3, name: 'Unavailable'},
  ];
  public resourceServiceState = new BehaviorSubject<boolean>(false);

  private _driverStatus: any[] = [];
  private _prestartFitcheckStatus: any[] = [];

  private _initiated = signal<boolean>(false);

  constructor(private http: HttpClient) {}

  public getAll (): Observable<ResponseModel | []> {
    let subject = new Subject<any>;
    this._initiated.set(true);
    this.http.get<ResponseModel>(`${appEnv.api}common-data`).pipe(
      map((response: ResponseModel) => {
        this._driverAccountTypes = (response.data.driver_account_type_list as Array<any>).map((obj:any) => DriverAccountTypeModel.toJson(obj));

        this._clients = this.rawClients = response.data.client_list ? (response.data?.client_list as Array<any>).map((data: any) => ClientModel.fromJson(data)) : [];
        
        this._vehicles = this.rawVehicles = (response.data?.vehicle_list as Array<any>).map((data: any) => VehicleModel.fromJson(data)).sort((a,b) => a.order - b.order);
        this._shiftTypes = this.rawShiftTypes = (response.data?.shift_type_list as Array<any>).map((data: any) => ShiftTypeModel.fromJson(data));
        
        this._roles = (response.data?.role_list as Array<any>).map(data => RoleModel.fromJson(data));

        const serviceTypes = (response.data?.service_type_list as Array<any>).map((data: any) => ServiceTypeModel.fromJson(data)).map(serviceType => {
          serviceType.roles = serviceType.roles.map(serviceRole => {
            const serviceRoleVehicleTypes = this._roles.find(role => role.id == serviceRole.id)?.vehicles || [];
            return {
              ...serviceRole,
              ...{
                vehicle_types: serviceRoleVehicleTypes
              }
            }
          });
          return serviceType;
        });
        const uniqServiceTypeMap = new Map();
        for (const serviceType of serviceTypes) {
          const key = `${serviceType.name}-${serviceType.categoryId}-${serviceType.machshipServiceId}`;
          if (!uniqServiceTypeMap.has(key)) {
            uniqServiceTypeMap.set(key, serviceType);
          }
        }
        
        this._serviceTypes = this.rawServiceTypes = Array.from(uniqServiceTypeMap.values());
        this._serviceTypes = this.rawServiceTypes;

        this._states = this.rawStates = (response.data?.state_list as Array<StateModel>).sort((a,b) => a.name.localeCompare(b.name));
        this._stopStatus = this.rawStopStatus = (response.data?.stop_status_list as Array<any>).map((data: any) => StopStatusModel.fromJson(data));
        this._rosterStatus = this.rawRosterStatus = (response.data?.roster_status_list as Array<RosterStatusModel>);
        this._tollOptions = this.rawTollOptions = (response.data?.toll_option_list as Array<TollOptionModel>);
        this._users = this.rawUsers = (response.data?.user_list as Array<any>).map((data: any) => UserModel.fromJson(data));
        this._stopTypes = this.rawStopTypes = (response.data?.stop_type_list as Array<StopTypeModel>);
        this._states = this.rawStates = (response.data?.state_list as Array<StateModel>);

        this._employeeTypes = (response.data?.employee_type_list as Array<any>).map(data => EmployeeTypeModel.fromJson(data)).map(employeeType => {
          employeeType.serviceTypes = employeeType.serviceTypes.map(empService => {
            const serviceRoles = this._serviceTypes.find(service => service.id == empService.id)?.roles || [];
            return {
              ...empService,
              ...{
                roles: serviceRoles
              }
            }
          })
          return employeeType;
        });
        
        this._vehicleClass = (response.data?.vehicle_class_list as Array<VehicleClassModel>);
        this.customerSubject.next(this._customer);
        this.stateSubject.next(this._states);
        this.employeeTypeSubject.next(this._employeeTypes);
        this._scheduleTypes = (response.data?.schedule_type_list as Array<any>);
        this._optimisationTypes = (response.data?.optimisation_type_list as Array<any>);
        
        this._drivers = this._rawDrivers = (response.data?.driver_list as Array<any>).map((data: any) => DriverModel.fromJson(data)).map(driver => {
          driver.roles = driver.roles.map(role => {
            const roleVehicles = this._roles.find(role => role.id === role.id)?.vehicles;
            return {
              ...role,
              vehicle_types: roleVehicles
            }
          });
          if (driver.assignedVehicle.length) {
            driver.assignedVehicle = driver.assignedVehicle.map(vehicle => {
              return {
                ...vehicle,
                id: vehicle?.vehicle_type_id,
                name: vehicle?.vehicle_detail?.vehicle_type_name,
              }
            });
          }

          const state = this._states.find(state => state.id == driver.stateId);
          driver.stateAbbr = state?.abbr || '';
          driver.stateName = state?.name || '';
          driver.profilePicture = driver.profilePicture ? `${appEnv.s3Url}${driver.profilePicture}` : ''
          return driver;
        }).sort((a,b) => (a.fullName ?? '').localeCompare((b.fullName ?? '')));
        this.driverSubject.next(this._drivers);

        this._customer = this.rawCustomer = (response.data?.customer_list as Array<any>).map((data: any) => CustomerModel.fromJson(data)).map(customer => {
          customer.branches = customer.branches.map(branch => {
            branch.services = (branch.services as Array<any>).filter(serviceId => {
              return this._serviceTypes.find(service => service.id == serviceId);
            }).map(serviceId => {
              return this._serviceTypes.find(service => service.id == serviceId)
            });
            branch.contractors = (branch.contractors as Array<any>).map(contractor => {
              const contractorObj = this._drivers.find(driver => driver.id === contractor.driver_id);
              if (contractorObj) {
                return contractorObj; 
              }
              return false;
            });
            branch.managerIds = (branch.managerIds as Array<any>).filter(userId => {
              const exists = this._users.find(u => u.id == userId);
              return typeof exists !== 'undefined';
            });
            return branch;
          })
          return customer;
        }).sort((a,b) => a.businessName.localeCompare(b.businessName));

        this._irTypes = response.data?.irtype_list;
        this._irStatus = response.data?.irstatus_list;

        this._serviceCategory = (response.data?.service_category_list as Array<any>);

        this._driverStatus = (response.data?.driver_status_list as Array<any>);
        this._prestartFitcheckStatus = (response.data?.prestart_fitcheck_status_list as Array<any>);

        return response;
      }),
      catchError(error => {
        /* Let the next page retry; callers skip getAll() while initiated */
        this._initiated.set(false);
        subject.error(error.error?.message || error.message || 'Update roster failed. Please try again.');
        return [];
      })
    ).subscribe((response: ResponseModel | []) => {
      this.resourceServiceState.next(true);
      subject.next(response);
      subject.complete();
    });

    return subject;
  }

  public get initiated () {
    return this._initiated;
  }


  public get clients (): ClientModel[] {
    return this._clients;
  }

  public get drivers (): DriverModel[] {
    return this._drivers;
  }

  public get vehicles (): VehicleModel[] {
    return this._vehicles;
  }

  public get shiftTypes (): ShiftTypeModel[] {
    return this._shiftTypes;
  }

  public get serviceTypes (): ServiceTypeModel[] {
    return this._serviceTypes;
  }

  public get states (): StateModel[] {
    return this._states;
  }

  public get stopStatus (): StopStatusModel[] {
    return this._stopStatus;
  }

  public get stopTypes (): StopTypeModel[] {
    return this._stopTypes;
  }

  public get rosterStatus (): RosterStatusModel[] {
    return this._rosterStatus;
  }

  public get tollOptions (): TollOptionModel[] {
    return this._tollOptions;
  }

  public get users (): UserModel[] {
    return this._users;
  }

  public get employeeTypes (): EmployeeTypeModel[] {
    return this._employeeTypes;
  }

  public get vehicleClass (): VehicleClassModel[] {
    return this._vehicleClass;
  }

  public get scheduleTypes (): ScheduleTypeModel[] {
    return this._scheduleTypes;
  }

  public get serviceCategories (): ServiceCategoryModel[] {
    return this._serviceCategory;
  }

  public get optimisationTypes (): ServiceCategoryModel[] {
    return this._optimisationTypes;
  }

  public get roles (): RoleModel[] {
    return this._roles;
  }

  public get customers (): CustomerModel[] {
    return this._customer;
  }

  public get paidType () {
    return this._paidType;
  }

  public get customerBranch (): any[] {
    const cBranch: any = [];
    this.customers.map(customer => {
      if (customer.branches.length) {
        customer.branches.forEach(branch => {
          cBranch.push({...branch,clientName: customer.businessName,clientId: customer.id, isTest: customer.isTest});
        })
      }
    });
    return cBranch;
  }

  public get driverStatus (): any[] {
    return this._driverStatus;
  } 

  public get irTypes () {
    return this._irTypes;
  }

  public get prestartFitcheckStatus (): any[] {
    return this._prestartFitcheckStatus;
  }

  public get irStatus () {
    return this._irStatus
  }

  public get availability () {
    return this._availability;
  }

  public get rawDrivers () {
    return this._rawDrivers;
  }

  public get driverAccountTypes () {
    return this._driverAccountTypes;
  }

  updateVehicleList (newValues: VehicleModel[] = []) {
    this._vehicles = [...newValues];
  }
}
